/**
 * Security Features — security context + cryptographic capabilities
 * + access control mechanisms + security monitoring + secure boot.
 * Pulls from `formatted.security_features`. Always renders.
 */
import React from 'react';
import { SectionCard, SubSection, Field, StringListField, ObjectListField, type SectionProps } from './parts';

const PATH = ['security_features'];

const yesNo = (v: unknown): string => (v === true ? 'true' : v === false ? 'false' : '');
const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

export default function SecurityFeatures({ data, isEditing, onChange }: SectionProps) {
  const sf = data ?? {};
  const ctx = sf.security_context ?? {};
  const crypto = sf.cryptographic_capabilities?.capabilities ?? [];
  const accessControl = sf.access_control?.mechanisms ?? [];
  const monitoring = sf.security_monitoring?.capabilities ?? [];
  const secureBoot = sf.secure_boot ?? {};

  const cryptoColumns = [
    { key: 'algorithm', label: 'Algorithm' },
    { key: 'key_size', label: 'Key Size' },
    { key: 'purpose', label: 'Purpose' },
    { key: 'implementation', label: 'Implementation' },
    { key: 'key_management', label: 'Key Management' },
    { key: 'compliance_standards', label: 'Compliance', render: joinList },
  ];
  const accessControlColumns = [
    { key: 'mechanism_type', label: 'Type' },
    { key: 'roles_defined', label: 'Roles', render: joinList },
    { key: 'privilege_levels', label: 'Privilege Levels', render: joinList },
    { key: 'controlled_resources', label: 'Controlled Resources', render: joinList },
    { key: 'enforcement_point', label: 'Enforcement' },
  ];
  const monitoringColumns = [
    { key: 'capability_type', label: 'Type' },
    { key: 'monitored_events', label: 'Monitored Events', render: joinList },
    { key: 'detection_methods', label: 'Detection Methods', render: joinList },
    { key: 'response_actions', label: 'Response Actions', render: joinList },
    { key: 'logging_details', label: 'Logging' },
  ];

  return (
    <SectionCard title="Security Features" id="security_features">
      <SubSection title="Security context">
        <StringListField label="Standards" items={ctx.standards} isEditing={isEditing} onChange={onChange} path={[...PATH, 'security_context', 'standards']} />
        <StringListField label="Encryption methods" items={ctx.encryption_methods} isEditing={isEditing} onChange={onChange} path={[...PATH, 'security_context', 'encryption_methods']} />
        <StringListField label="Authentication mechanisms" items={ctx.authentication_mechanisms} isEditing={isEditing} onChange={onChange} path={[...PATH, 'security_context', 'authentication_mechanisms']} />
        <StringListField label="Access control measures" items={ctx.access_control_measures} isEditing={isEditing} onChange={onChange} path={[...PATH, 'security_context', 'access_control_measures']} />
        <StringListField label="Security assumptions" items={ctx.security_assumptions} isEditing={isEditing} onChange={onChange} path={[...PATH, 'security_context', 'security_assumptions']} />
        <StringListField label="Threat indicators" items={ctx.threat_indicators} isEditing={isEditing} onChange={onChange} path={[...PATH, 'security_context', 'threat_indicators']} />
      </SubSection>

      <SubSection title="Cryptographic capabilities" count={crypto.length}>
        <ObjectListField columns={cryptoColumns} rows={crypto} isEditing={isEditing} onChange={onChange} path={[...PATH, 'cryptographic_capabilities', 'capabilities']} />
      </SubSection>

      <SubSection title="Access control mechanisms" count={accessControl.length}>
        <ObjectListField columns={accessControlColumns} rows={accessControl} isEditing={isEditing} onChange={onChange} path={[...PATH, 'access_control', 'mechanisms']} />
      </SubSection>

      <SubSection title="Security monitoring" count={monitoring.length}>
        <ObjectListField columns={monitoringColumns} rows={monitoring} isEditing={isEditing} onChange={onChange} path={[...PATH, 'security_monitoring', 'capabilities']} />
      </SubSection>

      <SubSection title="Secure boot">
        <Field label="Boot sequence protection" value={secureBoot.boot_sequence_protection} isEditing={isEditing} onChange={onChange} path={[...PATH, 'secure_boot', 'boot_sequence_protection']} />
        <Field label="Root of trust" value={secureBoot.root_of_trust} isEditing={isEditing} onChange={onChange} path={[...PATH, 'secure_boot', 'root_of_trust']} />
        <StringListField label="Verification stages" items={secureBoot.verification_stages} isEditing={isEditing} onChange={onChange} path={[...PATH, 'secure_boot', 'verification_stages']} />
        <Field label="Recovery mechanism" value={secureBoot.recovery_mechanism} isEditing={isEditing} onChange={onChange} path={[...PATH, 'secure_boot', 'recovery_mechanism']} multiline />
        <Field label="Anti-rollback" value={yesNo(secureBoot.anti_rollback)} isEditing={isEditing} onChange={onChange} path={[...PATH, 'secure_boot', 'anti_rollback']} />
      </SubSection>
    </SectionCard>
  );
}
